import { createSlice } from "@reduxjs/toolkit";
import { getSessions, deleteSession, terminateAllSessions } from "../thunks/sessionThunk";
import { ISessionState } from "../types/session";

const initialState: ISessionState = {
  sessions: [],
  totalActiveSessions: 0,
  loading: {
    getSessions: false,
    deleteSession: false,
    terminateAllSessions: false,
  },
  error: {
    getSessions: null,
    deleteSession: null,
    terminateAllSessions: null,
  },
  lastDeletedSessionId: null,
  lastTerminateMessage: null,
};

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    clearSessions: (state) => {
      state.sessions = [];
      state.totalActiveSessions = 0;
    },
    clearErrors: (state) => {
      state.error = {
        getSessions: null,
        deleteSession: null,
        terminateAllSessions: null,
      };
    },
    clearLastDeletedSessionId: (state) => {
      state.lastDeletedSessionId = null;
    },
    clearLastTerminateMessage: (state) => {
      state.lastTerminateMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Sessions
      .addCase(getSessions.pending, (state) => {
        state.loading.getSessions = true;
        state.error.getSessions = null;
      })
      .addCase(getSessions.fulfilled, (state, action) => {
        state.loading.getSessions = false;
        if (action.payload.success) {
          state.sessions = action.payload.data.sessions;
          state.totalActiveSessions = action.payload.data.totalActiveSessions;
        }
      })
      .addCase(getSessions.rejected, (state, action) => {
        state.loading.getSessions = false;
        state.error.getSessions = action.payload as string;
      })
      
      // Delete Session
      .addCase(deleteSession.pending, (state) => {
        state.loading.deleteSession = true;
        state.error.deleteSession = null;
      })
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.loading.deleteSession = false;
        if (action.payload.success) {
          state.lastDeletedSessionId = action.payload.data.sessionId;
          // Remove deleted session from the list
          state.sessions = state.sessions.filter(
            session => session.id !== action.payload.data.sessionId
          );
          state.totalActiveSessions = Math.max(0, state.totalActiveSessions - 1);
        }
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.loading.deleteSession = false;
        state.error.deleteSession = action.payload as string;
      })
      
      // Terminate All Sessions
      .addCase(terminateAllSessions.pending, (state) => {
        state.loading.terminateAllSessions = true;
        state.error.terminateAllSessions = null;
      })
      .addCase(terminateAllSessions.fulfilled, (state, action) => {
        state.loading.terminateAllSessions = false;
        if (action.payload.success) {
          state.lastTerminateMessage = action.payload.data.message;
          // Clear all sessions
          state.sessions = [];
          state.totalActiveSessions = 0;
        }
      })
      .addCase(terminateAllSessions.rejected, (state, action) => {
        state.loading.terminateAllSessions = false;
        state.error.terminateAllSessions = action.payload as string;
      });
  }
});

export const { 
  clearSessions, 
  clearErrors, 
  clearLastDeletedSessionId, 
  clearLastTerminateMessage 
} = sessionSlice.actions;

export default sessionSlice.reducer; 